import * as d3 from 'd3';

import { IMargins } from '../../models/IMargins';
import { Margins } from '../../models/Margins';
import { IChartOptions } from '../../models/IChartOptions';
import { IChartBase } from '../interfaces/IChartBase';
import { IPlotArea, ILegend, ITitle, Title, Legend, PlotArea } from '../pieces';
import { IAxis, VerticalAxis, HorizontalAxis } from '../pieces/axes';
import { ILogger, LoggerFactory, GuidHelper } from '../../helpers/helpers';
import { SvgManager } from '../SvgManager';
import { ChartFactory } from '../factories/ChartFactory';
import { setSizeClasses } from './setSizeClasses';

export class ChartBase implements IChartBase {
    private _group: d3.Selection<any>;
    private _hasAxes: boolean;
    private _height: number;
    private _legend: ILegend;
    private _loggerChartBase: ILogger;
    private _margins: IMargins;
    private _options: IChartOptions;
    private _plotArea: IPlotArea;
    private _plotAreaGroup: d3.Selection<any>;
    private _plotAreaHeight: number;
    private _plotAreaWidth: number;
    private _resizeFunction;
    private _seriesGroup: d3.Selection<any>;
    private _title: ITitle;
    private _width: number;
    private _xAxisBase: IAxis;
    private _yAxisBase: IAxis;

    constructor(options: IChartOptions, svgManager: SvgManager, classed: string, hasAxes: boolean) {
        this._loggerChartBase = LoggerFactory.getLogger('WideTech.Charting.chartBase', false, 0);
        this._hasAxes = hasAxes;
        this.options(options);
        const size = svgManager.getSize();
        this.width(size.width);
        this.height(size.height);
        this.margins(new Margins(0, 0, 20, 0));
        this.initBase(svgManager.container(), classed);
        svgManager.addResizeFunction(this.resize());
        svgManager.addChart();
    }

    private initBase(container: d3.Selection<any>, classed: string): void {
        this._group = container.append('g')
            .classed(classed, true);
        const pane = this._group.append('g')
            .classed('panes', true)
            .append('g')
            .classed('pane', true)
            .classed('default-pane', true);
        const factory = new ChartFactory(d3, console);
        const id = GuidHelper.getGuid();
        this._plotArea = new PlotArea(pane, this.plotAreaWidth(), this.plotAreaHeight());
        this._plotAreaGroup = this._plotArea.group();
        this._seriesGroup = factory.createSeriesGroup(this._plotAreaGroup, this._plotArea.clipPathId());
        this._title = new Title(pane);
        this.title().translate((this.plotAreaWidth() / 2), 0);
        this._legend = new Legend(pane);
        this.legend()
            .translate(this.width() - this._margins.marginLeft - this._margins.marginRight, this.plotAreaHeight() / 2);
        if (this._hasAxes) {
            const axesGroup = this._plotAreaGroup
                .append('g')
                .classed('axes-group', true);
            this._yAxisBase = new VerticalAxis(this.plotAreaHeight(), 0, 1, 5, axesGroup);
            this._xAxisBase = new HorizontalAxis(this.plotAreaWidth(), 0, 1, 5, axesGroup);
        }
    }

    private logError(e) {
        this._loggerChartBase.error(e);
    }

    resize(): { (width: number, height: number): void } {
        const self = this;
        return (width: number, height: number) => {
            self._loggerChartBase.debug(`Resize - width:  ${width}; height: ${height}`);
            self.width(width);
            self.height(height);

            const ratio = this.ratio() || 1;
            const margins: IMargins = {
                marginBottom: this._margins.marginBottom * ratio,
                marginTop: this._margins.marginTop * ratio,
                marginLeft: this._margins.marginLeft * ratio,
                marginRight: this._margins.marginRight * ratio,
            };
            setSizeClasses(this.group(), width, height, ratio);

            const chartAreaWidth = width - margins.marginLeft - margins.marginRight;
            const chartAreaHeight = height - margins.marginTop - margins.marginBottom;
            self._legend.resize(chartAreaWidth, chartAreaHeight);
            const pa = this.resizePlotArea(chartAreaWidth, chartAreaHeight);
            const titleHeight = this.title().height() * ratio;
            this._legend.translate(chartAreaWidth, pa.plotAreaHeight / 2 + titleHeight);
            this._title.translate(pa.plotAreaWidth / 2, 0);
            if (self.resizeFunction()) {
                self.resizeFunction()(self);
            }
        };
    }

    private resizePlotArea(chartAreaWidth: number, chartAreaHeight: number): { plotAreaWidth: number, plotAreaHeight: number } {
        const ratio = this.ratio() || 1;
        const translateX = this.translateX();
        const translateY = this.translateY();
        this._plotAreaGroup
            .attr({
                'transform': `translate(${translateX}, ${translateY})`
            });
        const plotAreaWidth = chartAreaWidth - translateX - (this.legend().isVisible() ? this._legend.width() + 10 * ratio : 0);
        const titleHeight = this._title.height() * ratio;
        const xAxisHeight = (this._hasAxes && this.xAxis().isTitleVisible() ? this.xAxis().height() * ratio : 0);
        const plotAreaHeight = chartAreaHeight - translateY - titleHeight - xAxisHeight;
        this._plotArea.resizeFunction()(plotAreaWidth, plotAreaHeight);
        if (this._hasAxes) {
            this.yAxis().resize(plotAreaHeight);
            this.xAxis().resize(plotAreaWidth);
            this.yAxis().width();
            this.xAxis().height();
            this.xAxis().translate(0, plotAreaHeight);
        }
        this._plotAreaWidth = plotAreaWidth;
        this._plotAreaHeight = plotAreaHeight;
        const result = { plotAreaWidth, plotAreaHeight };
        return result;
    }

    moveAxes() {
        this.yAxis().resize(this.plotAreaHeight());
        this.xAxis().resize(this.plotAreaWidth());
        this.xAxis().translate(0, this.plotAreaHeight());
    }

    options(value?: IChartOptions): IChartOptions {
        if (arguments.length) {
            this._options = value;
        }
        return this._options;
    }

    width(value?: number): number {
        if (value) {
            this._width = value;
        }
        return this._width;
    }

    height(value?: number): number {
        if (value) {
            this._height = value;
        }
        return this._height;
    }

    group(): d3.Selection<any> {
        return this._group;
    }

    plotAreaGroup(): d3.Selection<any> {
        return this._plotAreaGroup;
    }

    plotArea() {
        return this._plotArea;
    }

    legend(): ILegend {
        return this._legend;
    }

    title(): ITitle {
        return this._title;
    }

    seriesGroup(): d3.Selection<any> {
        return this._seriesGroup;
    }

    executeHandled(method) {
        try {
            method();
        } catch (e) {
            this.logError(e);
        }
    }

    titleText(value?: string): string {
        return this.title().text(value);
    }

    plotAreaWidth(): number {
        return this._plotAreaWidth;
    }

    plotAreaHeight(): number {
        return this._plotAreaHeight;
    }

    translateX(value?: number): number {
        if (value !== undefined && value !== null) {
            this.options().translateX = value;
        }
        const ratio = this.ratio() || 1;
        let translateX = this.options().translateX * ratio;
        if (this._hasAxes && this.yAxis()) {
            translateX = this.yAxis().width() * ratio;
        }
        return translateX;
    }

    translateY(): number {
        let translateY = this.options().translateY;
        if (this._hasAxes && this.xAxis()) {
            translateY = this.options().translateY;
        }
        const ratio = this.ratio() || 1;
        translateY *= ratio;
        if (this.title() && this.title().isVisible()) {
            translateY += this.title().height() * ratio;
        }
        return translateY;
    }

    resizeFunction(value?) {
        if (value) {
            this._resizeFunction = value;
        }
        return this._resizeFunction;
    }

    isTitleVisible(value?: boolean): boolean {
        const visible = this.title().isVisible(value);
        this.resize()(this.width(), this.height());
        return visible;
    }

    isLegendVisible(value?: boolean): boolean {
        const visible = this.legend()
            ? this.legend().isVisible(value)
            : false;
        const t = this.legend();
        if (this.legend()) {
            this.resize()(this.width(), this.height());
        }
        return visible;
    }

    margins(value?: IMargins): IMargins {
        if (value) {
            this._margins = value;
        }
        return this._margins;
    }

    xAxis() {
        return this._xAxisBase;
    }

    yAxis() {
        return this._yAxisBase;
    }

    private _ratio: number = 1;
    ratio(): number;
    ratio(value: number): this;
    ratio(value?: number) {
        if (arguments.length) {
            this._ratio = value;
            const legend = this.legend();
            if (legend) {
                legend.ratio(this._ratio);
            }
            const title = this.title();
            if (title) {
                title.ratio(this._ratio);
            }
            if (this._xAxisBase) {
                this._xAxisBase.ratio(this._ratio);
            }
            if (this._yAxisBase) {
                this._yAxisBase.ratio(this._ratio);
            }
            return this;
        }
        return this._ratio;
    }
}